import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { ClipboardCheck, Plus } from 'lucide-react';
import { formatDate } from '../utils/dateUtils';

import { authHeaders } from '../lib/authToken';
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminInspections() {
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ order_id: '', product_id: '', resultat: 'conforme', notes: '', montant_retenue: '' });
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND}/api/inspections`, { headers: authHeaders() }).then(r => r.json()).then(d => setInspections(Array.isArray(d) ? d : [])).catch((err) => { /* eslint-disable-line no-empty */ }).finally(() => setLoading(false));
  }, []);

  async function createInspection() {
    const res = await fetch(`${BACKEND}/api/inspections`, {
      method: 'POST', headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ ...form, montant_retenue: form.montant_retenue ? parseFloat(form.montant_retenue) : null, checklist: [], photos: [] })
    });
    if (res.ok) { toast.success('Inspection créée'); setShowForm(false); }
  }

  const RESULT_COLORS = { conforme: '#3DDC97', maintenance: '#FFCC66', retenue_caution: '#FF5C6C' };
  const RESULT_LABELS = { conforme: 'Conforme', maintenance: 'Maintenance', retenue_caution: 'Retenue caution' };

  return (
    <div data-testid="admin-page">
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Produits</p>
        <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
          letterSpacing: '-.6px', color: 'var(--admin-text)', marginTop: 4 }}>Inspections matériel</h2>
        <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>Contrôles et inspections du parc matériel</p>
      </div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-xl font-bold" style={{ color: 'var(--admin-text)' }}>Inspections</h2>
        <button onClick={() => setShowForm(true)} className="btn-gold px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2">
          <Plus size={16} /> Nouvelle inspection
        </button>
      </div>

      {showForm && (
        <div className="mb-6 p-5 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid rgba(212,175,55,0.2)' }}>
          <h3 className="font-semibold text-sm mb-4" style={{ color: 'var(--admin-gold)' }}>Nouvelle inspection retour</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="block text-xs mb-1" style={{ color: 'var(--admin-muted)' }}>ID Commande</label>
              <input type="text" value={form.order_id} onChange={e => setForm(p => ({ ...p, order_id: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-xl text-sm" style={{ background: '#1C1C1C', border: '1px solid #2A2A2A', color: 'var(--admin-text)' }} /></div>
            <div><label className="block text-xs mb-1" style={{ color: 'var(--admin-muted)' }}>Résultat</label>
              <select value={form.resultat} onChange={e => setForm(p => ({ ...p, resultat: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-xl text-sm" style={{ background: '#1C1C1C', border: '1px solid #2A2A2A', color: 'var(--admin-text)' }}>
                <option value="conforme">Conforme</option>
                <option value="maintenance">Maintenance nécessaire</option>
                <option value="retenue_caution">Retenue caution</option>
              </select></div>
            {form.resultat === 'retenue_caution' && (
              <div><label className="block text-xs mb-1" style={{ color: 'var(--admin-muted)' }}>Montant retenu (€)</label>
                <input type="number" value={form.montant_retenue} onChange={e => setForm(p => ({ ...p, montant_retenue: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-xl text-sm" style={{ background: '#1C1C1C', border: '1px solid #2A2A2A', color: 'var(--admin-text)' }} /></div>
            )}
            <div className="sm:col-span-2"><label className="block text-xs mb-1" style={{ color: 'var(--admin-muted)' }}>Notes</label>
              <textarea rows={2} value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-xl text-sm resize-none" style={{ background: '#1C1C1C', border: '1px solid #2A2A2A', color: 'var(--admin-text)' }} /></div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-xl text-sm" style={{ background: '#2A2A2A', color: 'rgba(255,255,255,0.6)' }}>Annuler</button>
            <button onClick={createInspection} className="flex-1 btn-gold py-2 rounded-xl text-sm font-semibold">Enregistrer l'inspection</button>
          </div>
        </div>
      )}

      {loading ? <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 rounded-full" style={{ border: '2px solid #2A2A2A', borderTopColor: '#D4AF37' }} /></div> : (
        inspections.length === 0 ? (
          <div className="p-12 rounded-xl text-center" style={{ background: 'var(--admin-surface)', border: '1px solid #2A2A2A' }}>
            <ClipboardCheck size={40} className="mx-auto mb-3" style={{ color: 'rgba(255,255,255,0.15)' }} />
            <p className="text-sm" style={{ color: 'var(--admin-faint)' }}>Aucune inspection enregistrée</p>
          </div>
        ) : (
          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #2A2A2A' }}>
            {inspections.map((ins, i) => (
              <div key={ins.id} className="flex items-center gap-3 px-4 py-3" style={{ background: i % 2 === 0 ? '#161616' : '#121212', borderBottom: '1px solid rgba(42,42,42,0.5)' }}>
                <div className="flex-1">
                  <p className="text-sm font-semibold" style={{ color: 'var(--admin-text)' }}>Commande: {ins.order_id}</p>
                  <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{formatDate(ins.created_at)}</p>
                </div>
                <span className="px-2 py-1 rounded-full text-xs" style={{ background: `${RESULT_COLORS[ins.resultat]}15`, color: RESULT_COLORS[ins.resultat] }}>
                  {RESULT_LABELS[ins.resultat]}
                </span>
                {ins.montant_retenue && <span className="price-gold text-sm">{ins.montant_retenue}€</span>}
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
